import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getImbConfigStatus } from "@/lib/payments.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Save, Loader2, CheckCircle2, XCircle } from "lucide-react";


export const Route = createFileRoute("/admin/settings")({ component: SettingsAdmin });

const BUCKET = "app-assets";

function SettingsAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin_settings"],
    queryFn: async () => {
      const { data } = await supabase.from("app_settings").select("*");
      const map: Record<string, any> = {};
      for (const r of data ?? []) map[r.key] = r.value;
      return map;
    },
  });

  const [branding, setBranding] = useState({ app_name: "", logo_url: "", tagline: "" });
  const [wallet, setWallet] = useState({ min_add: 100, max_add: 50000, min_withdraw: 200, max_withdraw: 25000, upi_id: "", upi_qr_url: "" });
  const [contact, setContact] = useState({ whatsapp: "", telegram: "", customer_care: "", website: "" });
  
  const [system, setSystem] = useState({ maintenance: false, maintenance_message: "App is under maintenance. Please try later." });
  const [betting, setBetting] = useState({ min_bet: 10, max_bet: 10000, max_numbers: 50, multiplier: 100 });
  const [notices, setNotices] = useState({ deposit: "", withdraw: "" });
  const [home, setHome] = useState({ banner_url: "" });
  const [appUpdate, setAppUpdate] = useState({
    current_app_version: "1.0.0",
    minimum_required_version: "",
    current_version_code: 0,
    minimum_required_version_code: 0,
    latest_apk_download_link: "",
    force_update_enabled: false,
    update_message: "New update available. Please update the app to continue.",
  });
  const [paymentContact, setPaymentContact] = useState<{ telegram_link: string; whatsapp_number: string; telegram_enabled: boolean; whatsapp_enabled: boolean }>({
    telegram_link: "",
    whatsapp_number: "",
    telegram_enabled: false,
    whatsapp_enabled: false,
  });
  const [withdraw, setWithdraw] = useState({
    enabled: true,
    open_time: "10:00",
    close_time: "22:00",
    closed_message: "Withdrawals are currently closed. Please try again during the allowed time window.",
  });
  const [numberLimits, setNumberLimits] = useState<Record<string, number>>({});
  const [depositMode, setDepositMode] = useState<{ mode: "manual" | "automatic" }>({ mode: "manual" });
  const [imb, setImb] = useState({
    enabled: false,
    environment: "test" as "test" | "live",
    min_amount: 0,
    max_amount: 0,
    timeout_minutes: 15,
    auto_recheck: true,
    manual_fallback: true,
    maintenance_message: "Automatic payments are temporarily unavailable. Please try again later.",
  });
  const imbStatusFn = useServerFn(getImbConfigStatus);
  const { data: imbStatus } = useQuery({
    queryKey: ["imb_config_status"],
    queryFn: () => imbStatusFn({ data: undefined as any }),
    retry: false,
  });


  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (!data) return;
    if (data.branding) setBranding((s) => ({ ...s, ...data.branding }));
    if (data.wallet) setWallet((s) => ({ ...s, ...data.wallet }));
    if (data.contact) setContact((s) => ({ ...s, ...data.contact }));
    
    if (data.system) setSystem((s) => ({ ...s, ...data.system }));
    if (data.betting) setBetting((s) => ({ ...s, ...data.betting }));
    if (data.notices) setNotices((s) => ({ ...s, ...data.notices }));
    if (data.home) setHome((s) => ({ ...s, ...data.home }));
    if (data.app_update) setAppUpdate((s) => ({ ...s, ...data.app_update }));
    if (data.payment_contact) setPaymentContact((s) => ({ ...s, ...data.payment_contact }));
    if (data.withdraw) setWithdraw((s) => ({ ...s, ...data.withdraw }));
    if (data.number_limits && typeof data.number_limits === "object") {
      const clean: Record<string, number> = {};
      for (const [k, v] of Object.entries(data.number_limits as Record<string, unknown>)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) clean[k] = n;
      }
      setNumberLimits(clean);
    }
    if (data.deposit_mode) setDepositMode((s) => ({ ...s, ...data.deposit_mode }));
    if (data.imb_gateway) setImb((s) => ({ ...s, ...data.imb_gateway }));


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function save(key: string, value: any) {
    const { error } = await supabase.from("app_settings").upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) { toast.error(error.message); return false; }
    return true;
  }

  function normalizedAppUpdate() {
    return {
      current_app_version: String(appUpdate.current_app_version || ""),
      minimum_required_version: String(appUpdate.minimum_required_version || ""),
      current_version_code: parseInt(String(appUpdate.current_version_code || 0), 10) || 0,
      minimum_required_version_code: parseInt(String(appUpdate.minimum_required_version_code || 0), 10) || 0,
      latest_apk_download_link: String(appUpdate.latest_apk_download_link || ""),
      force_update_enabled: Boolean(appUpdate.force_update_enabled),
      update_message: String(appUpdate.update_message || "New update available. Please update the app to continue."),
    };
  }

  async function saveAll() {
    setSavingAll(true);
    const results = await Promise.all([
      save("branding", branding),
      save("wallet", wallet),
      save("contact", contact),
      
      save("system", system),
      save("betting", betting),
      save("notices", notices),
      save("home", home),
      save("app_update", normalizedAppUpdate()),
      save("payment_contact", paymentContact),
      save("withdraw", withdraw),
      save("number_limits", numberLimits),
      save("deposit_mode", { mode: depositMode.mode === "automatic" ? "automatic" : "manual" }),
      save("imb_gateway", {
        ...imb,
        min_amount: Number(imb.min_amount) || 0,
        max_amount: Number(imb.max_amount) || 0,
        timeout_minutes: Math.max(1, Number(imb.timeout_minutes) || 15),
      }),


    ]);
    setSavingAll(false);
    if (results.every(Boolean)) {
      toast.success("All settings saved");
      qc.invalidateQueries();
    }
  }

  async function uploadImage(file: File, prefix: string): Promise<string | null> {
    const ext = file.name.split(".").pop() || "png";
    const path = `${prefix}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true, cacheControl: "3600" });
    if (error) { toast.error(error.message); return null; }
    // Signed URL for private bucket (10 years)
    const { data: signed, error: sErr } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (sErr || !signed) { toast.error(sErr?.message || "Failed to sign URL"); return null; }
    return signed.signedUrl;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">App Settings</h1>
        <Button onClick={saveAll} disabled={savingAll} className="gradient-primary">
          {savingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save All
        </Button>
      </div>

      <Section title="Branding">
        <Field label="App Name"><Input value={branding.app_name} onChange={(e) => setBranding({ ...branding, app_name: e.target.value })} /></Field>
        <Field label="Tagline"><Input value={branding.tagline} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} /></Field>
        <ImageField
          label="App Logo"
          url={branding.logo_url}
          onChange={(url) => setBranding({ ...branding, logo_url: url })}
          upload={(f) => uploadImage(f, "logo")}
        />
      </Section>

      <Section title="Contact & Support">
        <Field label="Customer Care Mobile Number"><Input value={contact.customer_care} onChange={(e) => setContact({ ...contact, customer_care: e.target.value })} placeholder="+91…" /></Field>
        <Field label="WhatsApp Number / URL"><Input value={contact.whatsapp} onChange={(e) => setContact({ ...contact, whatsapp: e.target.value })} placeholder="https://wa.me/…" /></Field>
        <Field label="Telegram Link"><Input value={contact.telegram} onChange={(e) => setContact({ ...contact, telegram: e.target.value })} placeholder="https://t.me/…" /></Field>
        <Field label="Website Link"><Input value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} placeholder="https://…" /></Field>
      </Section>

      <Section title="Betting Rules">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Minimum Bet Amount (₹)"><Input type="number" value={betting.min_bet} onChange={(e) => setBetting({ ...betting, min_bet: Number(e.target.value) })} /></Field>
          <Field label="Maximum Bet Amount (₹)"><Input type="number" value={betting.max_bet} onChange={(e) => setBetting({ ...betting, max_bet: Number(e.target.value) })} /></Field>
          <Field label="Max Numbers per Bet"><Input type="number" value={betting.max_numbers} onChange={(e) => setBetting({ ...betting, max_numbers: Number(e.target.value) })} /></Field>
          <Field label="Winning Multiplier (x)"><Input type="number" value={betting.multiplier} onChange={(e) => setBetting({ ...betting, multiplier: Number(e.target.value) })} /></Field>
        </div>
        <p className="text-[11px] text-muted-foreground">Multiplier applies to new results. Default: 100×.</p>
      </Section>

      <Section title="Per-Number Limits (Per Game / Day)">
        <p className="text-[11px] text-muted-foreground">
          Cap the total amount a single user can bet on each number (00–99) per game, per day (Asia/Kolkata). Limits are NOT combined across games — each game tracks its own totals and resets the next day. Leave blank or 0 for no limit. When a user's total on a number in a game would exceed this, the bet is rejected with “Limit reached for this number in this game.”

        </p>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-10">
          {Array.from({ length: 100 }, (_, i) => {
            const key = String(i);
            const val = numberLimits[key];
            return (
              <div key={key} className="flex flex-col items-stretch">
                <label className="text-center text-[10px] font-semibold text-muted-foreground">
                  {i.toString().padStart(2, "0")}
                </label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="—"
                  value={val ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setNumberLimits((prev) => {
                      const next = { ...prev };
                      if (raw === "") { delete next[key]; return next; }
                      const n = Number(raw);
                      if (!Number.isFinite(n) || n <= 0) { delete next[key]; return next; }
                      next[key] = n;
                      return next;
                    });
                  }}
                  className="h-8 px-1 text-center text-xs"
                />
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNumberLimits({})}
          >
            Clear all limits
          </Button>
        </div>
      </Section>


      <Section title="Wallet & Payments">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Minimum Deposit (₹)"><Input type="number" value={wallet.min_add} onChange={(e) => setWallet({ ...wallet, min_add: Number(e.target.value) })} /></Field>
          <Field label="Maximum Deposit (₹)"><Input type="number" value={wallet.max_add} onChange={(e) => setWallet({ ...wallet, max_add: Number(e.target.value) })} /></Field>
          <Field label="Minimum Withdraw (₹)"><Input type="number" value={wallet.min_withdraw} onChange={(e) => setWallet({ ...wallet, min_withdraw: Number(e.target.value) })} /></Field>
          <Field label="Maximum Withdraw (₹)"><Input type="number" value={wallet.max_withdraw} onChange={(e) => setWallet({ ...wallet, max_withdraw: Number(e.target.value) })} /></Field>
        </div>
        <Field label="UPI ID"><Input value={wallet.upi_id} onChange={(e) => setWallet({ ...wallet, upi_id: e.target.value })} placeholder="name@bank" /></Field>
        <ImageField
          label="UPI QR Image"
          url={wallet.upi_qr_url}
          onChange={(url) => setWallet({ ...wallet, upi_qr_url: url })}
          upload={(f) => uploadImage(f, "upi-qr")}
        />
      </Section>


      <Section title="Notices">
        <Field label="Deposit Notice"><Textarea rows={3} value={notices.deposit} onChange={(e) => setNotices({ ...notices, deposit: e.target.value })} placeholder="Shown on the deposit page" /></Field>
        <Field label="Withdrawal Notice"><Textarea rows={3} value={notices.withdraw} onChange={(e) => setNotices({ ...notices, withdraw: e.target.value })} placeholder="Shown on the withdraw page" /></Field>
      </Section>

      <Section title="Home Banner">
        <ImageField
          label="Home Banner Image"
          url={home.banner_url}
          onChange={(url) => setHome({ banner_url: url })}
          upload={(f) => uploadImage(f, "home-banner")}
        />
      </Section>

      <Section title="Maintenance Mode">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Maintenance Mode</p>
            <p className="text-[11px] text-muted-foreground">When ON, users see the maintenance message. Admins retain full access.</p>
          </div>
          <Switch checked={system.maintenance} onCheckedChange={(v) => setSystem({ ...system, maintenance: v })} />
        </div>
        <Field label="Maintenance Message"><Input value={system.maintenance_message} onChange={(e) => setSystem({ ...system, maintenance_message: e.target.value })} /></Field>
      </Section>

      <Section title="Force App Update">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Force Update Enabled</p>
            <p className="text-[11px] text-muted-foreground">When ON, users on versions below the minimum required must update before using the app.</p>
          </div>
          <Switch checked={appUpdate.force_update_enabled} onCheckedChange={(v) => setAppUpdate({ ...appUpdate, force_update_enabled: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Current App Version"><Input value={appUpdate.current_app_version} onChange={(e) => setAppUpdate({ ...appUpdate, current_app_version: e.target.value })} placeholder="1.6" /></Field>
          <Field label="Minimum Required Version"><Input value={appUpdate.minimum_required_version} onChange={(e) => setAppUpdate({ ...appUpdate, minimum_required_version: e.target.value })} placeholder="1.6" /></Field>
          <Field label="Current Version Code"><Input type="number" inputMode="numeric" value={String(appUpdate.current_version_code || "")} onChange={(e) => setAppUpdate({ ...appUpdate, current_version_code: parseInt(e.target.value, 10) || 0 })} placeholder="6" /></Field>
          <Field label="Minimum Required Version Code"><Input type="number" inputMode="numeric" value={String(appUpdate.minimum_required_version_code || "")} onChange={(e) => setAppUpdate({ ...appUpdate, minimum_required_version_code: parseInt(e.target.value, 10) || 0 })} placeholder="6" /></Field>
        </div>
        <Field label="Latest APK Download Link"><Input value={appUpdate.latest_apk_download_link} onChange={(e) => setAppUpdate({ ...appUpdate, latest_apk_download_link: e.target.value })} placeholder="https://…/app.apk" /></Field>
        <Field label="Update Message"><Input value={appUpdate.update_message} onChange={(e) => setAppUpdate({ ...appUpdate, update_message: e.target.value })} /></Field>
      </Section>

      <Section title="Deposit Mode">
        <p className="text-[11px] text-muted-foreground">
          Controls what happens when a player taps <b>Add Money</b>. Manual keeps the existing Telegram / WhatsApp
          deposit flow exactly as it is today. Automatic sends players to the IMB UPI Gateway.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {(["manual", "automatic"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setDepositMode({ mode: m })}
              className={
                "rounded-xl border p-3 text-left transition-colors " +
                (depositMode.mode === m ? "border-primary bg-primary/10" : "hover:bg-accent")
              }
            >
              <p className="text-sm font-bold">{m === "manual" ? "Manual Deposit" : "Automatic Deposit"}</p>
              <p className="text-[11px] text-muted-foreground">
                {m === "manual" ? "Telegram / WhatsApp + admin credit" : "IMB UPI Gateway + auto credit"}
              </p>
            </button>
          ))}
        </div>
        {depositMode.mode === "automatic" && !imbStatus?.tokenPresent && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-[11px]">
            Automatic mode is selected but the IMB API token (<b>IMB_USER_TOKEN</b>) is not stored yet. Players stay on
            the manual flow until it is added.
          </div>
        )}

      </Section>

      <Section title="IMB Automatic Payment Settings">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Gateway Enabled</p>
            <p className="text-[11px] text-muted-foreground">Master switch for the IMB UPI Gateway.</p>
          </div>
          <Switch checked={imb.enabled} onCheckedChange={(v) => setImb({ ...imb, enabled: v })} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {(["test", "live"] as const).map((env) => (
            <button
              key={env}
              type="button"
              onClick={() => setImb({ ...imb, environment: env })}
              className={
                "rounded-xl border p-3 text-center text-sm font-bold uppercase transition-colors " +
                (imb.environment === env ? "border-primary bg-primary/10" : "hover:bg-accent")
              }
            >
              {env} Mode
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Minimum Auto Deposit (₹)">
            <Input type="number" value={String(imb.min_amount || "")} onChange={(e) => setImb({ ...imb, min_amount: Number(e.target.value) })} placeholder="0 = use wallet limits" />
          </Field>
          <Field label="Maximum Auto Deposit (₹)">
            <Input type="number" value={String(imb.max_amount || "")} onChange={(e) => setImb({ ...imb, max_amount: Number(e.target.value) })} placeholder="0 = use wallet limits" />
          </Field>
          <Field label="Payment Timeout (minutes)">
            <Input type="number" value={String(imb.timeout_minutes || "")} onChange={(e) => setImb({ ...imb, timeout_minutes: Number(e.target.value) })} placeholder="15" />
          </Field>
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Auto Re-check Pending Payments</p>
            <p className="text-[11px] text-muted-foreground">Re-verify with the gateway before crediting.</p>
          </div>
          <Switch checked={imb.auto_recheck} onCheckedChange={(v) => setImb({ ...imb, auto_recheck: v })} />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Manual Fallback</p>
            <p className="text-[11px] text-muted-foreground">If the gateway fails, offer the Telegram / WhatsApp flow.</p>
          </div>
          <Switch checked={imb.manual_fallback} onCheckedChange={(v) => setImb({ ...imb, manual_fallback: v })} />
        </div>
        <Field label="Gateway Maintenance Message">
          <Textarea value={imb.maintenance_message} onChange={(e) => setImb({ ...imb, maintenance_message: e.target.value })} rows={2} />
        </Field>

        <div className="rounded-xl border bg-muted/40 p-3">
          <p className="text-xs font-bold">Gateway URLs (registered with IMB)</p>
          <div className="mt-2 space-y-1 text-[11px] break-all">
            <p><span className="text-muted-foreground">API base: </span>{imbStatus?.apiBase ?? "https://api.imbpay.in/v2"}</p>
            <p><span className="text-muted-foreground">Webhook / Callback: </span>{imbStatus?.webhookUrl ?? "https://brothersclub.co.in/api/public/imb-webhook"}</p>
            <p><span className="text-muted-foreground">Redirect / Return: </span>{imbStatus?.returnUrl ?? "https://brothersclub.co.in/payment/return"}</p>
          </div>
        </div>

        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3">
          <p className="text-xs font-bold">Official IMB contract mapped</p>
          <ul className="mt-2 space-y-1 text-[11px]">
            {[
              "Create Order: POST /create-order (form-urlencoded, user_token in body)",
              "Check Status: POST /check-order-status (user_token + order_id)",
              "Success requires status COMPLETED + txnStatus COMPLETED + result SUCCESS",
              "Webhook is notification-only — credit happens only after Check Status",
              "Wallet credited exactly once by credit_automatic_deposit_once()",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border bg-muted/40 p-3">
          <p className="text-xs font-bold">Cloud Secrets Status</p>
          <div className="mt-2 space-y-1 text-[11px]">
            <SecretRow label="API token (IMB_USER_TOKEN)" ok={!!imbStatus?.tokenPresent} missing={imbStatus?.test?.missing} />
            <SecretRow label="Test mode ready" ok={!!imbStatus?.test?.ready} missing={imbStatus?.test?.missing} />
            <SecretRow label="Live mode enabled (IMB_LIVE_MODE_ENABLED)" ok={!!imbStatus?.liveEnabled} missing={imbStatus?.live?.missing} />
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">
            Secret values are never shown here — only whether they are present. Live mode stays blocked until
            IMB_LIVE_MODE_ENABLED is set to true.
          </p>
        </div>

      </Section>

      <Section title="Payment Contact Settings">

        <p className="text-[11px] text-muted-foreground">
          Users tap Add Money and are handed off to the enabled channels below with a prefilled deposit message containing their User ID, mobile number, and requested amount. After the user sends payment proof, credit their wallet manually from Admin → Users.
        </p>
        <Field label="Telegram Link">
          <Input
            value={paymentContact.telegram_link}
            onChange={(e) => setPaymentContact({ ...paymentContact, telegram_link: e.target.value })}
            placeholder="https://t.me/yourusername or @yourusername"
          />
        </Field>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Telegram Enabled</p>
            <p className="text-[11px] text-muted-foreground">Show the Telegram deposit button on Add Money.</p>
          </div>
          <Switch
            checked={paymentContact.telegram_enabled}
            onCheckedChange={(v) => setPaymentContact({ ...paymentContact, telegram_enabled: v })}
          />
        </div>
        <Field label="WhatsApp Number">
          <Input
            value={paymentContact.whatsapp_number}
            onChange={(e) => setPaymentContact({ ...paymentContact, whatsapp_number: e.target.value })}
            placeholder="91XXXXXXXXXX (with country code, digits only)"
          />
        </Field>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">WhatsApp Enabled</p>
            <p className="text-[11px] text-muted-foreground">Show the WhatsApp deposit button on Add Money.</p>
          </div>
          <Switch
            checked={paymentContact.whatsapp_enabled}
            onCheckedChange={(v) => setPaymentContact({ ...paymentContact, whatsapp_enabled: v })}
          />
        </div>
      </Section>



      <Section title="Withdraw Settings">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-semibold">Withdraw ON / OFF</p>
            <p className="text-[11px] text-muted-foreground">When OFF, users see the closed message and cannot submit withdraw requests.</p>
          </div>
          <Switch checked={withdraw.enabled} onCheckedChange={(v) => setWithdraw({ ...withdraw, enabled: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Withdraw Open Time (IST)">
            <Input type="time" value={withdraw.open_time} onChange={(e) => setWithdraw({ ...withdraw, open_time: e.target.value })} />
          </Field>
          <Field label="Withdraw Close Time (IST)">
            <Input type="time" value={withdraw.close_time} onChange={(e) => setWithdraw({ ...withdraw, close_time: e.target.value })} />
          </Field>
        </div>
        <Field label="Withdraw Closed Message">
          <Textarea rows={2} value={withdraw.closed_message} onChange={(e) => setWithdraw({ ...withdraw, closed_message: e.target.value })} />
        </Field>
        <p className="text-[11px] text-muted-foreground">Timezone is fixed to Asia/Kolkata. Enforced on both the app and the database.</p>
      </Section>


      <div className="sticky bottom-4 z-20 flex justify-end">
        <Button onClick={saveAll} disabled={savingAll} size="lg" className="gradient-primary shadow-purple">
          {savingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save All Settings
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: any) {
  return (
    <div className="space-y-3 rounded-2xl bg-card p-4 shadow-card">
      <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }: any) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function ImageField({
  label, url, onChange, upload,
}: { label: string; url: string; onChange: (url: string) => void; upload: (f: File) => Promise<string | null> }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const u = await upload(f);
    setBusy(false);
    if (u) { onChange(u); toast.success(`${label} uploaded`); }
    if (ref.current) ref.current.value = "";
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-start gap-3">
        <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border bg-muted/50">
          {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-muted-foreground">No image</span>}
        </div>
        <div className="flex-1 space-y-2">
          <Input placeholder="Paste image URL or upload" value={url} onChange={(e) => onChange(e.target.value)} />
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}>
            {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-2 h-3.5 w-3.5" />}
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
}

function SecretRow({ label, ok, missing }: { label: string; ok: boolean; missing?: string[] }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={"flex items-center gap-1 font-semibold " + (ok ? "text-emerald-600" : "text-destructive")}>
        {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
        {ok ? "Ready" : missing?.length ? `Missing ${missing.length}` : "Not set"}
      </span>
    </div>
  );
}
